import { useState, useMemo, useEffect } from 'react';
import { View, Text, TextInput, TouchableOpacity, ScrollView, StyleSheet, useWindowDimensions, ActivityIndicator, Linking } from 'react-native';
import { Helmet } from 'react-helmet-async';
import { useAppTheme } from '../../utils/useAppTheme';
import WebNumberMeaning from '../components/WebNumberMeaning';
import WebVerseReference from '../components/WebVerseReference';
import { ExplainableMetric } from '../../components/MetricExplanationModal';
import DailyInsight from '../../components/DailyInsight';
import {
  calculateLifePathNumber,
  getLifePathMeaning,
  calculateBirthdayNumber,
  getWesternZodiac,
  getChineseZodiac,
  getChineseYearElement,
  getYinYang,
  getDayOfWeek,
  getDayOfWeekMeaning,
  calculateAge,
  getNextBirthday,
  getBirthstone,
  getBirthFlower,
  getBirthSeason,
  calculateBirthdayDigitSum,
  calculateDigitalRoot,
  getBibleMatches,
  fetchFamousBirthdays,
  fetchHistoricalEvents,
} from '../../utils/dobCalculations';

export default function DOBCalculatorPage() {
  const { colors } = useAppTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);
  const { width } = useWindowDimensions();
  const isMobile = width < 768;

  const [month, setMonth] = useState('7');
  const [day, setDay] = useState('15');
  const [year, setYear] = useState('1992');
  const [expandedSections, setExpandedSections] = useState({
    dailyInsight: true,
    numerology: true,
    bible: true,
    western: true,
    chinese: true,
    birthdays: true,
    events: true,
    age: true,
    extras: true,
  });
  const [famousBirthdays, setFamousBirthdays] = useState([]);
  const [historicalEvents, setHistoricalEvents] = useState([]);
  const [loadingBirthdays, setLoadingBirthdays] = useState(false);
  const [loadingEvents, setLoadingEvents] = useState(false);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Fetch from APIs when month/day changes
  useEffect(() => {
    const m = parseInt(month);
    const d = parseInt(day);
    if (m >= 1 && m <= 12 && d >= 1 && d <= 31) {
      setLoadingBirthdays(true);
      setLoadingEvents(true);
      fetchFamousBirthdays(month, day).then(data => {
        setFamousBirthdays(data);
        setLoadingBirthdays(false);
      });
      fetchHistoricalEvents(month, day).then(data => {
        setHistoricalEvents(data);
        setLoadingEvents(false);
      });
    }
  }, [month, day]);

  const isValidDate = () => {
    const m = parseInt(month);
    const d = parseInt(day);
    const y = parseInt(year);
    return m >= 1 && m <= 12 && d >= 1 && d <= 31 && y >= 1900 && y <= 2100;
  };

  const results = useMemo(() => {
    if (!isValidDate()) return null;

    const lifePath = calculateLifePathNumber(month, day, year);
    const lifePathMeaning = getLifePathMeaning(lifePath);
    const birthdayNumber = calculateBirthdayNumber(day);
    const western = getWesternZodiac(month, day);
    const chinese = getChineseZodiac(year);
    const yearElement = getChineseYearElement(year);
    const yinYang = getYinYang(year);
    const dayOfWeek = getDayOfWeek(month, day, year);
    const dayMeaning = getDayOfWeekMeaning(dayOfWeek);
    const age = calculateAge(month, day, year);
    const nextBday = getNextBirthday(month, day);
    const birthstone = getBirthstone(month);
    const birthFlower = getBirthFlower(month);
    const birthSeason = getBirthSeason(month);
    const digitSum = calculateBirthdayDigitSum(month, day, year);
    const digitalRoot = calculateDigitalRoot(digitSum);
    const bibleMatches = getBibleMatches(digitSum);

    return {
      lifePath, lifePathMeaning, birthdayNumber,
      western, chinese, yearElement, yinYang,
      dayOfWeek, dayMeaning,
      age, nextBday,
      birthstone, birthFlower, birthSeason,
      digitSum, digitalRoot, bibleMatches,
    };
  }, [month, day, year]);

  const renderInfoRow = (label, value, emoji, numberForMeaning, metricKey) => (
    <ExplainableMetric metricKey={metricKey} style={styles.infoRow} helpColor={colors.lightText}>
      <Text style={styles.infoLabel}>{emoji ? emoji + ' ' : ''}{label}</Text>
      <View style={styles.infoValueGroup}>
        <Text style={styles.infoValue}>{value}</Text>
        {numberForMeaning != null && <WebNumberMeaning number={numberForMeaning} />}
      </View>
    </ExplainableMetric>
  );

  const renderTagList = (items, bgColor, metricKey) => (
    <View style={styles.tagContainer}>
      {items.map((item, i) => (
        <ExplainableMetric
          key={i}
          metricKey={metricKey}
          style={[styles.tag, { backgroundColor: bgColor || colors.primarySurface }]}
          showIcon={false}
        >
          <Text style={[styles.tagText, { color: colors.primary }]}>{item}</Text>
        </ExplainableMetric>
      ))}
    </View>
  );

  const renderSubsectionLabel = (label, metricKey) => (
    <ExplainableMetric metricKey={metricKey} style={styles.explainableSubsection} helpColor={colors.lightText}>
      <Text style={styles.subLabel}>{label}</Text>
    </ExplainableMetric>
  );

  const renderSectionHeader = (title, sectionKey) => (
    <TouchableOpacity
      style={styles.sectionHeader}
      onPress={() => toggleSection(sectionKey)}
      activeOpacity={0.7}
    >
      <Text style={styles.sectionTitle}>{title}</Text>
      <Text style={styles.chevron}>{expandedSections[sectionKey] ? '▲' : '▼'}</Text>
    </TouchableOpacity>
  );

  return (
    <ScrollView style={styles.container} contentContainerStyle={[styles.scrollContent, isMobile && styles.scrollContentMobile]}>
      <Helmet>
        <title>Birthday Calculator - Gematria Calculator</title>
        <meta name="description" content="Discover your Life Path Number, Western Zodiac, Chinese Zodiac, birthstone, and more from your date of birth." />
      </Helmet>

      <Text style={styles.title}>Birthday Calculator</Text>
      <Text style={styles.subtitle}>Discover what your date of birth reveals about you</Text>

      {/* Date Input */}
      <View style={styles.inputCard}>
        <Text style={styles.inputLabel}>Enter Your Birthday</Text>
        <View style={styles.dateInputRow}>
          <TextInput
            style={styles.dateInput}
            placeholder="MM"
            placeholderTextColor={colors.lightText}
            value={month}
            onChangeText={setMonth}
            maxLength={2}
          />
          <Text style={styles.separator}>/</Text>
          <TextInput
            style={styles.dateInput}
            placeholder="DD"
            placeholderTextColor={colors.lightText}
            value={day}
            onChangeText={setDay}
            maxLength={2}
          />
          <Text style={styles.separator}>/</Text>
          <TextInput
            style={[styles.dateInput, { width: 70 }]}
            placeholder="YYYY"
            placeholderTextColor={colors.lightText}
            value={year}
            onChangeText={setYear}
            maxLength={4}
          />
        </View>
        {results && (
          <Text style={styles.datePreview}>
            {results.dayOfWeek}, {new Date(parseInt(year), parseInt(month) - 1, parseInt(day)).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
          </Text>
        )}
      </View>

      {results && (
        <View style={[styles.resultsGrid, isMobile && styles.resultsGridMobile]}>
          {/* Today's Insight */}
          <View style={[styles.card, isMobile && styles.cardMobile, { width: '100%' }]}>
            {renderSectionHeader("Today's Insight", 'dailyInsight')}
            {expandedSections.dailyInsight && (
              <DailyInsight
                birthMonth={month}
                birthDay={day}
                birthYear={year}
                colors={colors}
              />
            )}
          </View>

          {/* Numerology */}
          <View style={[styles.card, isMobile && styles.cardMobile]}>
            {renderSectionHeader('Numerology', 'numerology')}
            {expandedSections.numerology && (
              <View style={styles.cardContent}>
                <ExplainableMetric metricKey="lifePath" style={styles.highlightBox} iconStyle={styles.metricHelpBadge}>
                  <Text style={styles.highlightLabel}>LIFE PATH NUMBER</Text>
                  <View style={styles.highlightNumberRow}>
                    <Text style={styles.highlightNumber}>{results.lifePath}</Text>
                    <WebNumberMeaning number={results.lifePath} />
                  </View>
                  <Text style={styles.highlightTitle}>{results.lifePathMeaning.title}</Text>
                  <Text style={styles.highlightDesc}>{results.lifePathMeaning.description}</Text>
                  <View style={{ marginTop: 8 }}>{renderTagList(results.lifePathMeaning.traits)}</View>
                </ExplainableMetric>
                {renderInfoRow('Birthday Number', results.birthdayNumber, '🔢', results.birthdayNumber, 'birthdayNumber')}
                {renderInfoRow('Digit Sum', results.digitSum, '➕', results.digitSum, 'digitSum')}
                {renderInfoRow('Digital Root', results.digitalRoot, '🔄', results.digitalRoot, 'digitalRoot')}
                {renderInfoRow('Born on', results.dayOfWeek, '📅', null, 'bornOn')}
                <ExplainableMetric metricKey="weekdayPlanet" style={styles.subBox} iconStyle={styles.metricHelpBadge}>
                  <Text style={styles.subBoxTitle}>Planet: {results.dayMeaning.planet}</Text>
                  <Text style={styles.subBoxText}>{results.dayMeaning.trait}</Text>
                </ExplainableMetric>
              </View>
            )}
          </View>

          {/* Bible Connection */}
          <View style={[styles.card, isMobile && styles.cardMobile]}>
            {renderSectionHeader('Bible Connection', 'bible')}
            {expandedSections.bible && (
              <View style={styles.cardContent}>
                <ExplainableMetric metricKey="bibleConnection" style={styles.explainableNote} helpColor={colors.lightText}>
                  <Text style={styles.noteText}>
                    Your birthday digits ({(String(parseInt(month)) + String(parseInt(day)) + String(parseInt(year))).split('').join(' + ')}) = {results.digitSum}. Bible phrases with that same gematria value:
                  </Text>
                </ExplainableMetric>
                {Object.keys(results.bibleMatches).length > 0 ? (
                  Object.keys(results.bibleMatches).slice(0, 4).map(cipherName => {
                    const match = results.bibleMatches[cipherName];
                    return (
                      <View key={cipherName} style={styles.bibleCipherCard}>
                        <View style={styles.bibleCipherHeader}>
                          <Text style={styles.bibleCipherName}>{cipherName}</Text>
                          <Text style={styles.bibleCipherValue}>= {match.value}</Text>
                        </View>
                        {match.phrases.slice(0, 5).map((item, i) => (
                          <View style={styles.bibleItem} key={`${cipherName}-${i}`}>
                            <Text style={styles.bibleWord}>"{item.p}"</Text>
                            <WebVerseReference reference={item.r} label={`${item.r} ›`} />
                          </View>
                        ))}
                      </View>
                    );
                  })
                ) : (
                  <Text style={styles.emptyText}>No Bible phrase matches found for value {results.digitSum}.</Text>
                )}
              </View>
            )}
          </View>

          {/* Famous Birthdays */}
          <View style={[styles.card, isMobile && styles.cardMobile]}>
            {renderSectionHeader('Famous Birthdays', 'birthdays')}
            {expandedSections.birthdays && (
              <View style={styles.cardContent}>
                <Text style={styles.noteText}>Notable people also born on {parseInt(month)}/{parseInt(day)}</Text>
                {loadingBirthdays ? (
                  <ActivityIndicator size="small" color={colors.primary} style={{ marginVertical: 12 }} />
                ) : famousBirthdays.length > 0 ? (
                  famousBirthdays.map((person, i) => (
                    <TouchableOpacity
                      key={i}
                      style={styles.eventItem}
                      onPress={() => person.wikipedia && Linking.openURL(person.wikipedia)}
                      disabled={!person.wikipedia}
                    >
                      <Text style={styles.eventYear}>{person.year}</Text>
                      <Text style={styles.eventDesc}>{person.description}</Text>
                    </TouchableOpacity>
                  ))
                ) : (
                  <Text style={styles.emptyText}>No data available</Text>
                )}
              </View>
            )}
          </View>

          {/* Historical Events */}
          <View style={[styles.card, isMobile && styles.cardMobile]}>
            {renderSectionHeader('On This Day in History', 'events')}
            {expandedSections.events && (
              <View style={styles.cardContent}>
                <Text style={styles.noteText}>Historical events on {parseInt(month)}/{parseInt(day)}</Text>
                {loadingEvents ? (
                  <ActivityIndicator size="small" color={colors.primary} style={{ marginVertical: 12 }} />
                ) : historicalEvents.length > 0 ? (
                  historicalEvents.map((event, i) => (
                    <TouchableOpacity
                      key={i}
                      style={styles.eventItem}
                      onPress={() => event.wikipedia && Linking.openURL(event.wikipedia)}
                      disabled={!event.wikipedia}
                    >
                      <Text style={styles.eventYear}>{event.year}</Text>
                      <Text style={styles.eventDesc}>{event.description}</Text>
                    </TouchableOpacity>
                  ))
                ) : (
                  <Text style={styles.emptyText}>No data available</Text>
                )}
              </View>
            )}
          </View>

          {/* Western Zodiac */}
          <View style={[styles.card, isMobile && styles.cardMobile]}>
            {renderSectionHeader('Western Zodiac', 'western')}
            {expandedSections.western && (
              <View style={styles.cardContent}>
                <ExplainableMetric metricKey="westernSign" style={styles.highlightBox} iconStyle={styles.metricHelpBadge}>
                  <Text style={{ fontSize: 40 }}>{results.western.symbol}</Text>
                  <Text style={styles.highlightTitle}>{results.western.name}</Text>
                  <Text style={styles.highlightDesc}>{results.western.description}</Text>
                </ExplainableMetric>
                {renderInfoRow('Element', results.western.element, '🌍', null, 'westernElement')}
                {renderInfoRow('Modality', results.western.modality, '⚡', null, 'westernModality')}
                {renderInfoRow('Ruling Planet', results.western.rulingPlanet, '🪐', null, 'westernRulingPlanet')}
                {renderInfoRow('Lucky Day', results.western.luckyDay, '🍀', null, 'westernLuckyDay')}
                {renderInfoRow('Lucky Numbers', results.western.luckyNumbers.join(', '), '🔢', null, 'westernLuckyNumbers')}
                {renderInfoRow('Lucky Colors', results.western.luckyColors.join(', '), '🎨', null, 'westernLuckyColors')}
                {renderSubsectionLabel('Strengths', 'westernStrengths')}
                {renderTagList(results.western.strengths, undefined, 'westernStrengths')}
                {renderSubsectionLabel('Weaknesses', 'westernWeaknesses')}
                {renderTagList(results.western.weaknesses, colors.mutedSurface, 'westernWeaknesses')}
                {renderSubsectionLabel('Compatible Signs', 'westernCompatible')}
                {renderTagList(results.western.compatibleSigns, undefined, 'westernCompatible')}
                {renderSubsectionLabel('Least Compatible', 'westernLeastCompatible')}
                {renderTagList(results.western.leastCompatible, colors.mutedSurface, 'westernLeastCompatible')}
                {renderSubsectionLabel(`Famous ${results.western.name}`, 'westernFamous')}
                {renderTagList(results.western.famousPeople, undefined, 'westernFamous')}
              </View>
            )}
          </View>

          {/* Chinese Zodiac */}
          <View style={[styles.card, isMobile && styles.cardMobile]}>
            {renderSectionHeader('Chinese Zodiac', 'chinese')}
            {expandedSections.chinese && (
              <View style={styles.cardContent}>
                <ExplainableMetric metricKey="chineseSign" style={styles.highlightBox} iconStyle={styles.metricHelpBadge}>
                  <Text style={{ fontSize: 40 }}>{results.chinese.chinese}</Text>
                  <Text style={styles.highlightTitle}>{results.yearElement} {results.chinese.animal}</Text>
                  <Text style={styles.highlightSubtitle}>{results.chinese.pinyin} • {results.yinYang}</Text>
                  <Text style={styles.highlightDesc}>{results.chinese.description}</Text>
                </ExplainableMetric>
                {renderInfoRow('Fixed Element', results.chinese.element, '🌊', null, 'chineseFixedElement')}
                {renderInfoRow('Year Element', results.yearElement, '✨', null, 'chineseYearElement')}
                {renderInfoRow('Yin/Yang', results.yinYang, '☯️', null, 'chineseYinYang')}
                {renderInfoRow('Trine', results.chinese.trine + ' Trine', '🔺', null, 'chineseTrine')}
                {renderInfoRow('Lucky Numbers', results.chinese.luckyNumbers.join(', '), '🔢', null, 'chineseLuckyNumbers')}
                {renderInfoRow('Lucky Colors', results.chinese.luckyColors.join(', '), '🎨', null, 'chineseLuckyColors')}
                {renderSubsectionLabel('Strengths', 'chineseStrengths')}
                {renderTagList(results.chinese.strengths, undefined, 'chineseStrengths')}
                {renderSubsectionLabel('Weaknesses', 'chineseWeaknesses')}
                {renderTagList(results.chinese.weaknesses, colors.mutedSurface, 'chineseWeaknesses')}
                {renderSubsectionLabel('Best Matches', 'chineseCompatible')}
                {renderTagList(results.chinese.compatibleWith, undefined, 'chineseCompatible')}
                {renderSubsectionLabel('Least Compatible', 'chineseLeastCompatible')}
                {renderTagList(results.chinese.leastCompatible, colors.mutedSurface, 'chineseLeastCompatible')}
                {renderSubsectionLabel(`Famous ${results.chinese.animal} People`, 'chineseFamous')}
                {renderTagList(results.chinese.famousPeople, undefined, 'chineseFamous')}
              </View>
            )}
          </View>

          {/* Age & Extras */}
          <View style={[styles.card, isMobile && styles.cardMobile]}>
            {renderSectionHeader('Age & Extras', 'age')}
            {expandedSections.age && (
              <View style={styles.cardContent}>
                {renderInfoRow('Age', results.age.years + ' years', '🎂', null, 'age')}
                {renderInfoRow('Total Months', results.age.totalMonths.toLocaleString(), '📆', null, 'totalMonths')}
                {renderInfoRow('Total Weeks', results.age.totalWeeks.toLocaleString(), '📅', null, 'totalWeeks')}
                {renderInfoRow('Total Days', results.age.totalDays.toLocaleString(), '☀️', null, 'totalDays')}
                {renderInfoRow('Total Hours', results.age.totalHours.toLocaleString(), '⏰', null, 'totalHours')}
                {renderInfoRow('Next Birthday', results.nextBday.daysUntil + ' days away', '🎈', null, 'nextBirthday')}
                <View style={styles.divider} />
                {renderInfoRow('Birthstone', results.birthstone.stone, '💎', null, 'birthstone')}
                {renderInfoRow('Stone Meaning', results.birthstone.meaning, '✨', null, 'stoneMeaning')}
                {renderInfoRow('Birth Flower', results.birthFlower.flower, '🌺', null, 'birthFlower')}
                {renderInfoRow('Flower Meaning', results.birthFlower.meaning, '🌸', null, 'flowerMeaning')}
                <ExplainableMetric metricKey="birthSeason" style={styles.subBox} iconStyle={styles.metricHelpBadge}>
                  <Text style={styles.subBoxTitle}>{results.birthSeason.emoji} Born in {results.birthSeason.season}</Text>
                  <Text style={styles.subBoxText}>{results.birthSeason.trait}</Text>
                </ExplainableMetric>
              </View>
            )}
          </View>
        </View>
      )}
    </ScrollView>
  );
}

const createStyles = (colors) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background,
  },
  scrollContent: {
    padding: 24,
    maxWidth: 1200,
    alignSelf: 'center',
    width: '100%',
  },
  scrollContentMobile: {
    padding: 16,
  },
  title: {
    fontSize: 28,
    fontWeight: '700',
    color: colors.text,
    marginBottom: 4,
  },
  subtitle: {
    fontSize: 14,
    color: colors.lightText,
    marginBottom: 20,
  },
  inputCard: {
    backgroundColor: colors.surface,
    borderRadius: 12,
    padding: 20,
    marginBottom: 20,
    borderWidth: 1,
    borderColor: colors.border,
  },
  inputLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.text,
    marginBottom: 12,
  },
  dateInputRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  dateInput: {
    backgroundColor: colors.mutedSurface,
    borderRadius: 8,
    paddingVertical: 10,
    paddingHorizontal: 14,
    fontSize: 18,
    color: colors.text,
    textAlign: 'center',
    fontWeight: '600',
    borderWidth: 1,
    borderColor: colors.border,
    width: 55,
  },
  separator: {
    fontSize: 18,
    color: colors.lightText,
    marginHorizontal: 6,
  },
  datePreview: {
    fontSize: 13,
    color: colors.primary,
    marginTop: 10,
    fontWeight: '500',
  },
  resultsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 16,
  },
  resultsGridMobile: {
    flexDirection: 'column',
  },
  card: {
    backgroundColor: colors.surface,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: colors.border,
    overflow: 'hidden',
    width: '48.5%',
    minWidth: 340,
  },
  cardMobile: {
    width: '100%',
    minWidth: 'auto',
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: colors.text,
  },
  chevron: {
    fontSize: 12,
    color: colors.lightText,
  },
  cardContent: {
    padding: 16,
    paddingTop: 0,
  },
  highlightBox: {
    backgroundColor: colors.primarySurface,
    borderRadius: 10,
    padding: 16,
    alignItems: 'center',
    marginBottom: 12,
    borderWidth: 1,
    borderColor: colors.primary + '30',
  },
  highlightLabel: {
    fontSize: 10,
    color: colors.lightText,
    letterSpacing: 1.5,
    marginBottom: 4,
  },
  highlightNumber: {
    fontSize: 48,
    fontWeight: '800',
    color: colors.primary,
  },
  highlightTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: colors.text,
    marginBottom: 4,
    textAlign: 'center',
  },
  highlightSubtitle: {
    fontSize: 13,
    color: colors.lightText,
    marginBottom: 6,
  },
  highlightDesc: {
    fontSize: 13,
    color: colors.textSecondary,
    textAlign: 'center',
    lineHeight: 20,
  },
  infoRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 8,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.border,
  },
  metricHelpBadge: {
    position: 'absolute',
    top: 8,
    right: 8,
  },
  explainableNote: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 6,
  },
  explainableSubsection: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    gap: 6,
  },
  infoLabel: {
    fontSize: 13,
    color: colors.lightText,
    flex: 1,
  },
  infoValue: {
    fontSize: 13,
    fontWeight: '600',
    color: colors.text,
    textAlign: 'right',
    flex: 1,
  },
  subLabel: {
    fontSize: 11,
    fontWeight: '600',
    color: colors.lightText,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginTop: 12,
    marginBottom: 6,
  },
  tagContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
  },
  tag: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
  },
  tagText: {
    fontSize: 11,
    fontWeight: '500',
  },
  subBox: {
    backgroundColor: colors.mutedSurface,
    borderRadius: 8,
    padding: 12,
    marginTop: 10,
  },
  subBoxTitle: {
    fontSize: 13,
    fontWeight: '600',
    color: colors.text,
    marginBottom: 4,
  },
  subBoxText: {
    fontSize: 12,
    color: colors.textSecondary,
    lineHeight: 18,
  },
  divider: {
    height: 1,
    backgroundColor: colors.border,
    marginVertical: 12,
  },
  noteText: {
    fontSize: 12,
    color: colors.lightText,
    marginBottom: 10,
    fontStyle: 'italic',
  },
  bibleItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 6,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.border,
  },
  bibleWord: {
    fontSize: 13,
    fontWeight: '600',
    color: colors.text,
    flex: 1,
  },
  bibleRef: {
    fontSize: 11,
    color: colors.primary,
    marginLeft: 8,
  },
  eventItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    paddingVertical: 8,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.border,
    gap: 10,
  },
  eventYear: {
    fontSize: 13,
    fontWeight: '700',
    color: colors.primary,
    minWidth: 40,
  },
  eventDesc: {
    fontSize: 13,
    color: colors.text,
    flex: 1,
    lineHeight: 18,
  },
  emptyText: {
    fontSize: 13,
    color: colors.lightText,
    fontStyle: 'italic',
    textAlign: 'center',
    paddingVertical: 16,
  },
  highlightNumberRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  infoValueGroup: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
  bibleCipherCard: {
    backgroundColor: colors.mutedSurface,
    borderRadius: 8,
    marginBottom: 10,
    overflow: 'hidden',
  },
  bibleCipherHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingHorizontal: 10,
    paddingVertical: 7,
    backgroundColor: colors.primarySurface,
  },
  bibleCipherName: { fontSize: 12, fontWeight: '700', color: colors.text },
  bibleCipherValue: { fontSize: 12, fontWeight: '700', color: colors.primary },
});
